// =============================================================================
// features/sales/contacts/ContactRestrictionBanner.tsx — Banner de restrição
// de contato (doc 26 §7 `ContactRestrictionBanner`, §22 "o bloqueio é
// evidente e respeitado por todos os botões").
//
// Regra de acessibilidade (doc 26 §7 "foco, leitores de tela, contraste... em
// testes"): a restrição é comunicada por TEXTO — motivo + data — nunca só
// por cor/ícone. `role="alert"` garante que leitores de tela anunciem o
// banner ao aparecer, sem depender de o usuário "ver" o vermelho.
//
// `describeRestriction` é exportada separada do componente para ser testável
// sem precisar renderizar React (convenção deste repositório — ver
// __tests__/ScheduleAppointmentDialog ausente, OutcomeSelector.test.ts).
// =============================================================================

import type { ContactPreferenceChannelState } from '@elemento/shared-schemas';
import * as React from 'react';

import { formatOrgDate } from '../../../lib/orgDateTime';

import { CHANNEL_LABELS } from './labels';

export interface RestrictionLine {
  channel: string;
  channelLabel: string;
  reason: string;
  dateLabel: string | null;
}

function formatBlockedDate(iso: string | null): string | null {
  if (!iso) return null;
  const formatted = formatOrgDate(iso);
  return formatted.length > 0 ? formatted : null;
}

/**
 * Converte os registros bloqueados em linhas legíveis por humano — motivo
 * SEMPRE presente (cai para um texto genérico quando o registro não tem
 * `reason`, nunca deixa o motivo em branco).
 */
export function describeRestriction(blocked: ContactPreferenceChannelState[]): RestrictionLine[] {
  return blocked.map((pref) => ({
    channel: pref.channel,
    channelLabel: CHANNEL_LABELS[pref.channel],
    reason: pref.reason?.trim() ? pref.reason : 'Motivo não informado.',
    dateLabel: formatBlockedDate(pref.createdAt),
  }));
}

export interface ContactRestrictionBannerProps {
  blocked: ContactPreferenceChannelState[];
  /** `true` quando TODOS os canais estão bloqueados — muda o título/tom do banner. */
  isFullyBlocked: boolean;
  id?: string;
}

/**
 * Banner de restrição de contato. Não renderiza nada quando `blocked` está
 * vazio — o chamador decide se/onde montar o componente, mas a ausência de
 * conteúdo nunca ocupa espaço/gera ruído visual.
 */
export function ContactRestrictionBanner({
  blocked,
  isFullyBlocked,
  id,
}: ContactRestrictionBannerProps): React.JSX.Element | null {
  if (blocked.length === 0) return null;

  const lines = describeRestriction(blocked);

  return (
    <div
      id={id}
      role="alert"
      className="flex flex-col gap-3 rounded-md border-l-[3px] px-5 py-4"
      style={{
        borderColor: 'var(--danger)',
        background: 'var(--danger-bg)',
        boxShadow: 'var(--elev-2)',
      }}
    >
      <div className="flex items-start gap-3">
        <span
          aria-hidden="true"
          className="shrink-0 w-8 h-8 rounded-md flex items-center justify-center"
          style={{ background: 'var(--danger)', color: 'var(--text-on-brand)' }}
        >
          <svg
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.8}
            className="w-4 h-4"
          >
            <path d="M10 6.5v4.5M10 13.5v.01" strokeLinecap="round" />
            <path d="M8.6 3.3a1.6 1.6 0 0 1 2.8 0l6.4 11.2a1.6 1.6 0 0 1-1.4 2.4H3.6a1.6 1.6 0 0 1-1.4-2.4Z" />
          </svg>
        </span>

        <div className="flex flex-col gap-1 min-w-0">
          <p className="font-sans font-bold text-sm text-danger">
            {isFullyBlocked
              ? 'Contato bloqueado — todos os canais'
              : 'Contato parcialmente restrito'}
          </p>
          <p className="font-sans text-xs text-ink-2 leading-relaxed">
            {isFullyBlocked
              ? 'Este lead não pode ser contatado por nenhum canal. Ligar, agendar e registrar contato ficam desabilitados até o bloqueio ser revertido.'
              : 'Alguns canais deste lead estão bloqueados — os demais continuam disponíveis.'}
          </p>
        </div>
      </div>

      <ul className="flex flex-col gap-2 pl-11">
        {lines.map((line) => (
          <li key={line.channel} className="flex flex-col gap-0.5">
            <p className="font-sans text-xs font-semibold text-ink uppercase tracking-[0.06em]">
              {line.channelLabel} — bloqueado
            </p>
            <p className="font-sans text-xs text-ink-2">
              {line.reason}
              {line.dateLabel && <span className="text-ink-3"> · desde {line.dateLabel}</span>}
            </p>
          </li>
        ))}
      </ul>
    </div>
  );
}
